import { createFileRoute } from '@tanstack/react-router'
import { AlternativesPage, alternativesHead, type AlternativesData } from '@/components/alternatives-page'
import { MONITOR_LOCATIONS } from '@/lib/check-locations'

const data: AlternativesData = {
  tool: "Pingdom",
  slug: "pingdom",
  intro: "Pingdom, from SolarWinds, has no free plan and prices uptime by the number of checks. If you want a free plan, flat pricing, or broader observability, here are alternatives.",
  alternatives: [
    {
      name: "Better Stack",
      description: "Uptime monitoring combined with on-call, incident management, logs, and status pages in one platform.",
      pricing: "Free (10 monitors), paid from $34/mo per responder",
      pros: ["On-call scheduling and incident management", "Unlimited phone and SMS alerts on paid plans", "Multi-location confirmation (3 of 4 locations by default)"],
      cons: ["Priced per responder, plus monitor packs", "More product than you need if you only want uptime checks"],
      url: "https://betterstack.com",
    },
    {
      name: "UptimeRobot",
      description: "A widely used uptime monitor with a free plan of 50 monitors at 5-minute checks, now allowed for commercial use. 30-second checks start on the Team plan.",
      pricing: "Free (50 monitors), Solo $10/mo, Team $41/mo",
      pros: ["Free plan with 50 monitors, commercial use allowed", "Mobile apps for iOS and Android", "15-second checks on the Scale plan"],
      cons: ["Free plan checks every 5 minutes; Solo every 60 seconds", "30-second checks start at $41/mo (Team)"],
      url: "https://uptimerobot.com",
    },
    {
      name: "Uptrack",
      description: "Uptime monitoring with 30-second checks and alert confirmation. Pro includes 50 monitors for $3/mo.",
      pricing: "Free (5 monitors), Pro $3/mo, Team $20/mo, Business $79/mo",
      pros: ["30-second checks on every plan", "Alerts only after 2-5 consecutive failed checks", "Flat pricing with a 30-day money-back guarantee"],
      cons: ["Free plan is 5 monitors at 30 s from one location", MONITOR_LOCATIONS.alternativesCon, "No APM or logs"],
      url: "https://uptrack.app",
      isUptrack: true,
    },
    {
      name: "Site24x7",
      description: "Monitoring suite from Zoho covering websites, servers, APM, real user monitoring, and logs.",
      pricing: "Free (5 monitors), Web Uptime $10/mo",
      pros: ["APM, RUM, server, and log monitoring", "32 check locations on Web Uptime", "Status pages on paid plans"],
      cons: ["1-minute checks on entry plans", "30-second checks start on Enterprise Plus Web ($999/mo)"],
      url: "https://www.site24x7.com",
    },
    {
      name: "Datadog",
      description: "Full observability platform covering APM, logs, infrastructure, and synthetic monitoring, billed by usage.",
      pricing: "Synthetics: $5 per 10k API runs, $12 per 1k browser runs (annual billing)",
      pros: ["Full-stack observability in one platform", "API and browser synthetic tests", "Retries and multi-location alert rules"],
      cons: ["No free tier for Synthetics", "Usage-based billing varies month to month"],
      url: "https://www.datadoghq.com",
    },
    {
      name: "Checkly",
      description: "Monitoring as code with Playwright browser checks, API checks, and CI/CD integration.",
      pricing: "Free (Hobby), Starter $24/mo",
      pros: ["Monitoring as code with Playwright", "CI/CD integration", "Configurable retry strategies"],
      cons: ["API and browser runs above plan limits are billed per run", "30-second checks start on Team ($64/mo)"],
      url: "https://www.checklyhq.com",
    },
  ],
  comparisonTable: [
    { tool: "Better Stack", freePlan: "10 monitors", checkInterval: "3min (free), 30s (paid)", alertConfirmation: "Yes (multi-location)", startingPrice: "$34/mo per responder" },
    { tool: "UptimeRobot", freePlan: "50 monitors", checkInterval: "5min (free), 30s (Team)", alertConfirmation: "Multi-location for timeouts", startingPrice: "$10/mo" },
    { tool: "Uptrack", freePlan: "5 monitors at 30s", checkInterval: "30s (all plans)", alertConfirmation: "Yes (2-5 consecutive)", startingPrice: "Free; Pro $3/mo" },
    { tool: "Site24x7", freePlan: "5 monitors", checkInterval: "1min", alertConfirmation: "Configurable", startingPrice: "$10/mo" },
    { tool: "Datadog", freePlan: "None (synthetics)", checkInterval: "Per test", alertConfirmation: "Configurable", startingPrice: "$5 per 10k API runs" },
    { tool: "Checkly", freePlan: "Hobby (10 uptime monitors)", checkInterval: "2min (free), 30s (Team)", alertConfirmation: "Configurable retries", startingPrice: "$24/mo" },
  ],
}

export const Route = createFileRoute('/_site/alternatives/pingdom')({
  component: () => <AlternativesPage data={data} />,
  head: () => alternativesHead(data),
})
